import { useState } from 'react';
import { ChevronDown, Loader2, SlidersHorizontal } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { campaignsApi, type Campaign } from '../campaignsApi';
import { getAuthHeaders } from '../../../services/http';
import { useSessionStore } from '../../../stores/sessionStore';
import { campaignStatusOptions, campaignStatusHint } from '../campaignCardPolicy';
import './campaign-status.css';
import { CampaignOriginPicker } from './CampaignOriginPicker';
import { CheckCampaignStatusButton } from './CheckCampaignStatusButton';

export function CampaignStatusButton({ campaign }: { campaign: Campaign }) {
  const token = useSessionStore(state => state.session?.access_token);
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [linking, setLinking] = useState(false);
  const missingMetaOrigin = !campaign.meta_campaign_id
    && (campaign.meta_adset_id || campaign.meta_ad_id || !['google', 'google_ads'].includes(campaign.ad_platform || ''))
    && !['deleted', 'deleting', 'delete_failed', 'publicando', 'publishing', 'concluida', 'completed', 'arquivada', 'archived'].includes(campaign.status);
  const options = campaignStatusOptions(campaign);
  const changing = [campaign.meta_sync_status, campaign.google_sync_status]
    .some(status => ['status_changing', 'syncing', 'publishing'].includes(status || ''));
  const mutation = useMutation({
    mutationFn: (status: string) => campaignsApi.transition(campaign.id, status, {
      headers: getAuthHeaders(token!),
    }),
    onSuccess: () => setOpen(false),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      queryClient.invalidateQueries({ queryKey: ['campaign', campaign.id] });
    },
  });
  const pending = mutation.isPending || changing;
  const Icon = pending ? Loader2 : SlidersHorizontal;

  return <div className="campaign-status-control" onClick={event => event.stopPropagation()}>
    <button type="button" className="campaign-status-button" disabled={pending || !token}
      title="Alterar status diretamente neste card"
      aria-label={`Alterar status da campanha ${campaign.name}`}
      aria-expanded={open} aria-controls={`campaign-status-${campaign.id}`}
      aria-busy={pending} onClick={() => setOpen(!open)}>
      <Icon size={15} className={pending ? 'spin' : undefined} />
      {pending ? 'Sincronizando...' : 'Alterar status'} <ChevronDown size={14} />
    </button>
    {open && <div id={`campaign-status-${campaign.id}`} className="campaign-status-options">
      <span className="campaign-status-hint">{campaignStatusHint(campaign)}</span>
      {options.map(option => <button key={option.value} type="button" disabled={pending}
        onClick={() => mutation.mutate(option.value)}>{option.label}</button>)}
    </div>}
    {mutation.isError && <span role="alert" className="campaign-status-error">
      {mutation.error.message}
    </span>}
    {missingMetaOrigin && !linking && <button type="button" className="campaign-origin-link"
      disabled={pending || !token} onClick={() => setLinking(true)}>Vincular origem Meta</button>}
    {linking && <CampaignOriginPicker campaign={campaign} onClose={() => setLinking(false)} />}
    <CheckCampaignStatusButton campaign={campaign}/>
  </div>;
}
